import React, { useState } from 'react';
import { FaEdit, FaTrash, FaUser } from 'react-icons/fa';
import commentService from '../../services/commentService';

const CommentsList = ({ comments, currentUserId, onEdit, onDelete }) => {
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');

  const handleEditClick = (comment) => {
    setEditingCommentId(comment.id);
    setEditContent(comment.content);
    setError('');
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditContent('');
    setError('');
  };

  const handleSaveEdit = async (commentId) => {
    const trimmedContent = editContent.trim();

    if (!trimmedContent) {
      setError('Comment cannot be empty');
      return;
    }

    if (trimmedContent.length > 2000) {
      setError('Comment cannot exceed 2000 characters');
      return;
    }

    try {
      setIsEditing(true);
      setError('');

      const result = await commentService.updateComment(commentId, trimmedContent);

      if (result.success) {
        setEditingCommentId(null);
        setEditContent('');

        if (onEdit) {
          onEdit(result.comment);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to update comment');
    } finally {
      setIsEditing(false);
    }
  };

  const handleDeleteClick = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      await commentService.deleteComment(commentId);

      if (onDelete) {
        onDelete(commentId);
      }
    } catch (err) {
      alert(err.message || 'Failed to delete comment');
    }
  };

  if (!comments || comments.length === 0) {
    return (
      <div className="cs_comments_empty">
        <p>Be the first to comment!</p>
      </div>
    );
  }

  return (
    <div className="cs_comments_list">
      {comments.map((comment) => {
        const isOwnComment = currentUserId && comment.user?.id === currentUserId;
        const isEditMode = editingCommentId === comment.id;
        const userName = comment.user
          ? `${comment.user.first_name || ''} ${comment.user.last_name || ''}`.trim()
          : 'Anonymous';

        return (
          <div className="cs_comment" key={comment.id}>
            <div className="cs_comment_avatar">
              {comment.user?.avatar ? (
                <img
                  src={`http://localhost/cms/assets/${comment.user.avatar}`}
                  alt={userName}
                  className="cs_avatar_img"
                />
              ) : (
                <div className="cs_avatar_placeholder">
                  <FaUser />
                </div>
              )}
            </div>

            <div className="cs_comment_body">
              <div className="cs_comment_header">
                <span className="cs_comment_author">{userName}</span>
                <span className="cs_comment_date">
                  {commentService.formatRelativeTime(
                    comment.date_updated || comment.date_created
                  )}
                  {comment.date_updated &&
                    comment.date_updated !== comment.date_created &&
                    ' (edited)'}
                </span>
              </div>

              {isEditMode ? (
                <div className="cs_comment_edit">
                  <textarea
                    className="cs_comment_edit_textarea"
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows="3"
                    maxLength="2100"
                    disabled={isEditing}
                  />
                  {error && <div className="cs_comment_error">{error}</div>}
                  <div className="cs_comment_edit_actions">
                    <span className="cs_char_count">
                      {editContent.length}/2000
                    </span>
                    <button
                      className="cs_btn_save"
                      onClick={() => handleSaveEdit(comment.id)}
                      disabled={isEditing || !editContent.trim()}
                    >
                      {isEditing ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      className="cs_btn_cancel"
                      onClick={handleCancelEdit}
                      disabled={isEditing}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="cs_comment_content">{comment.content}</div>

                  {isOwnComment && (
                    <div className="cs_comment_actions">
                      <button
                        className="cs_comment_action_btn"
                        onClick={() => handleEditClick(comment)}
                        title="Edit comment"
                      >
                        <FaEdit /> Edit
                      </button>
                      <button
                        className="cs_comment_action_btn cs_comment_action_delete"
                        onClick={() => handleDeleteClick(comment.id)}
                        title="Delete comment"
                      >
                        <FaTrash /> Delete
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CommentsList;
